import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useUserStore } from '@/store/useUserStore';

interface WorkspaceBootGateProps {
  children: ReactNode;
}

/**
 * Ensures the super admin's workspace list is fetched and a default workspace
 * is selected before any workspace-scoped data loads. For non-super-admins
 * this is a pass-through — their workspace_id ships with the auth payload.
 *
 * Pair this with the api.ts interceptor: this gate covers the UI render path,
 * the interceptor covers any request that fires outside the React tree
 * (background hooks, retries, etc.).
 */
export const WorkspaceBootGate = ({ children }: WorkspaceBootGateProps) => {
  const isSuperAdmin = useUserStore((state) => state.isSuperAdmin);
  const workspacesLoaded = useUserStore((state) => state.workspacesLoaded);
  const workspaces = useUserStore((state) => state.workspaces);
  const selectedWorkspaceId = useUserStore((state) => state.selectedWorkspaceId);
  const bootstrapWorkspaces = useUserStore((state) => state.bootstrapWorkspaces);

  useEffect(() => {
    if (isSuperAdmin) {
      void bootstrapWorkspaces();
    }
  }, [isSuperAdmin, bootstrapWorkspaces]);

  if (!isSuperAdmin) return <>{children}</>;

  // Bootstrap still in flight on first render.
  if (!workspacesLoaded) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-[#F8F9FF]">
        <div className="flex flex-col items-center gap-3 text-[#556d83]">
          <Loader2 className="h-8 w-8 animate-spin text-[#3C3295]" />
          <p className="text-sm">Loading workspaces…</p>
        </div>
      </div>
    );
  }

  // Loaded, but no workspaces exist — super admin needs to create one before
  // any tenant-scoped UI can render.
  if (workspaces.length === 0) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-[#F8F9FF] p-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 max-w-md text-center space-y-4">
          <h2 className="text-lg font-semibold text-[#011427]">No workspaces yet</h2>
          <p className="text-sm text-[#556d83]">
            Create a workspace to start managing learning, users, and reports.
          </p>
          <Link
            to="/portal/workspaces/create"
            className="inline-block px-4 py-2 rounded-md bg-[#3C3295] text-white text-sm font-semibold hover:bg-[#322a7d] transition-colors"
          >
            Create workspace
          </Link>
        </div>
      </div>
    );
  }

  // Loaded with workspaces but no selection — bootstrap auto-selects the first
  // one, so this is a transient state. Keep the spinner up until it resolves.
  if (!selectedWorkspaceId) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-[#F8F9FF]">
        <div className="flex flex-col items-center gap-3 text-[#556d83]">
          <Loader2 className="h-8 w-8 animate-spin text-[#3C3295]" />
          <p className="text-sm">Selecting workspace…</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
